"use client";

import { CaretUpDownIcon, CheckIcon } from "@phosphor-icons/react";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useTranslation } from "@/i18n/client";
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { Typography } from "@/components/ui/typography";
import { cookieName, languages } from "@/i18n/settings";
import { buildLocalizedPath, getSupportedLanguage } from "@/utils/language";

const LANGUAGE_LABELS: Record<string, string> = {
  es: "Español",
  en: "Inglés",
  pt: "Portugués",
};

const LANGUAGE_FLAGS: Record<string, string> = {
  es: "ES",
  en: "US",
  pt: "BR",
};

const COOKIE_MAX_AGE_SECONDS = 31_536_000;

interface LanguageSwitcherMenuItemProps {
  onLanguageChange?: () => void;
}

export function LanguageSwitcherMenuItem(props: LanguageSwitcherMenuItemProps) {
  const { onLanguageChange } = props;
  const pathname = usePathname() ?? "/";
  const router = useRouter();
  const { i18n, t } = useTranslation("sidebar");
  const currentLanguage = getSupportedLanguage(i18n.resolvedLanguage ?? i18n.language);
  const currentFlag = LANGUAGE_FLAGS[currentLanguage];

  async function handleLanguageChange(nextLanguage: string): Promise<void> {
    const supportedLanguage = getSupportedLanguage(nextLanguage);
    if (supportedLanguage === currentLanguage) {
      return;
    }

    onLanguageChange?.();
    localStorage.setItem("i18nextLng", supportedLanguage);
    document.cookie = `${cookieName}=${supportedLanguage}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
    await i18n.changeLanguage(supportedLanguage);

    const nextPath = buildLocalizedPath(pathname, supportedLanguage);
    router.replace(`${nextPath}${window.location.search}`);
    router.refresh();
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="h-7 w-full cursor-pointer rounded-lg border border-primary-200 bg-primary-100 px-2 py-1 focus:bg-primary-100 data-[state=open]:bg-primary-100 [&>svg:last-child]:hidden">
        <div className="flex items-center gap-1.5 min-w-0">
          <Image
            src={`/images/flags/${currentFlag}.webp`}
            alt=""
            width={16}
            height={16}
            className="size-4 rounded-full object-cover"
          />
          <Typography variant="label 6" className="text-neutral-700">
            {LANGUAGE_LABELS[currentLanguage] ?? t("language")}
          </Typography>
        </div>
        <CaretUpDownIcon size={16} className="ml-auto text-neutral-600" />
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent
        className="z-200 flex w-[192px] flex-col gap-3 rounded-lg border border-primary-200 bg-primary-100 p-3 shadow-none"
      >
        {languages.map((language) => {
          const flag = LANGUAGE_FLAGS[language];
          const isSelected = language === currentLanguage;

          return (
            <DropdownMenuItem
              key={language}
              className="h-4 cursor-pointer gap-2 rounded-none p-0 focus:bg-transparent"
              onSelect={() => {
                void handleLanguageChange(language);
              }}
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <Image
                  src={`/images/flags/${flag}.webp`}
                  alt=""
                  width={16}
                  height={16}
                  className="size-4 rounded-full object-cover"
                />
                <Typography variant="label 6" className="text-neutral-700">
                  {LANGUAGE_LABELS[language]}
                </Typography>
              </div>
              {isSelected && <CheckIcon size={16} className="text-primary-600" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
